import { useEffect, useState } from "react";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, Building2, Briefcase, PlusCircle, FileText, Save, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const TABS = [["dashboard","Dashboard"],["profile","Company Profile"],["jobs","Job Orders"]];

export default function EmployerDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [company, setCompany] = useState(null);

  const load = async () => {
    try {
      const { data: s } = await api.get("/employer-portal/status");
      setStatus(s);
      if (s.user?.employer_status !== "APPROVED") return;
      const [{data: j}, {data: c}] = await Promise.all([
        api.get("/employer/job-orders"),
        api.get(`/companies/${s.user.company_id}`),
      ]);
      setJobs(j); setCompany(c);
    } catch (e) { console.error("EmployerDashboard load failed", e); }
  };
  useEffect(() => { load(); }, []);

  if (!status) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (status.user?.employer_status !== "APPROVED") {
    // Non-approved employers fall through to the existing EmployerStatus page via routing guard
    window.location.href = "/employer-portal/status";
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center"><InfinityMark size={22}/></div>
            <div><div className="font-display font-bold text-sm">Connect<span className="text-teal-300">My</span>Hub<span className="text-teal-300 text-[10px] font-semibold ml-0.5">™</span></div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">Employer Portal</div></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right"><div className="text-xs font-semibold">{user?.name}</div><div className="text-[10px] text-slate-400">{status.company?.name}</div></div>
            <button onClick={logout} data-testid="emp-logout" className="text-xs flex items-center gap-1 border border-slate-700 rounded-md px-3 py-1.5"><LogOut size={12}/> Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-sky-600">Welcome</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">{status.company?.name}</h1>
        <div className="border-b border-slate-200 mb-6 flex gap-1">
          {TABS.map(([id,lbl])=>(
            <button key={id} data-testid={`emp-nav-${id}`} onClick={()=>setTab(id)} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab===id?"border-sky-600 text-slate-900":"border-transparent text-slate-500"}`}>{lbl}</button>
          ))}
        </div>

        {tab==="dashboard" && (
          <div className="grid md:grid-cols-3 gap-4">
            <Stat label="Open jobs" value={jobs.filter(j=>j.status==="Open").length} icon={Briefcase} onClick={()=>setTab("jobs")}/>
            <Stat label="Drafts" value={jobs.filter(j=>j.status==="Draft").length} icon={FileText} onClick={()=>setTab("jobs")}/>
            <Stat label="Company profile" value={company?.description?"Complete":"Fill in"} icon={Building2} onClick={()=>setTab("profile")}/>
            <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-slate-900">Get started</h3></div>
              <div className="grid md:grid-cols-2 gap-3">
                <Link to="/employer/job-builder" data-testid="cta-job-builder" className="p-4 border-2 border-sky-200 rounded-lg hover:bg-sky-50 flex items-start gap-3">
                  <PlusCircle size={20} className="text-sky-600 shrink-0 mt-0.5"/>
                  <div><div className="font-semibold text-slate-900">Create a Job Order</div><div className="text-xs text-slate-500 mt-0.5">Upload / paste a JD, import from URL, start from template, or build with ConnectMyHub.</div></div>
                </Link>
                <Link to="/employer/matches" data-testid="cta-matches" className="p-4 border-2 border-emerald-200 rounded-lg hover:bg-emerald-50 flex items-start gap-3">
                  <FileText size={20} className="text-emerald-600 shrink-0 mt-0.5"/>
                  <div><div className="font-semibold text-slate-900">Find my best matches</div><div className="text-xs text-slate-500 mt-0.5">Curated shortlist of Qualified/Premier talent for your open roles.</div></div>
                </Link>
                <Link to="/employer/workforce-assessment" data-testid="cta-wa" className="p-4 border-2 border-amber-200 rounded-lg hover:bg-amber-50 flex items-start gap-3">
                  <Briefcase size={20} className="text-amber-600 shrink-0 mt-0.5"/>
                  <div><div className="font-semibold text-slate-900">Help me build my workforce</div><div className="text-xs text-slate-500 mt-0.5">ConnectMyHub recommends Hire / Managed / AI / Hybrid / Train / Outsource.</div></div>
                </Link>
                <button onClick={()=>setTab("profile")} data-testid="cta-profile" className="p-4 border-2 border-slate-200 rounded-lg hover:bg-slate-50 flex items-start gap-3 text-left">
                  <Building2 size={20} className="text-slate-600 shrink-0 mt-0.5"/>
                  <div><div className="font-semibold text-slate-900">Complete your Company Profile</div><div className="text-xs text-slate-500 mt-0.5">Enter once, ConnectMyHub reuses it across every Job Order.</div></div>
                </button>
                <Link to="/employer/interviews" data-testid="cta-interviews" className="p-4 border-2 border-slate-300 rounded-lg hover:bg-slate-100 flex items-start gap-3">
                  <FileText size={20} className="text-slate-700 shrink-0 mt-0.5"/>
                  <div><div className="font-semibold text-slate-900">Your interviews</div><div className="text-xs text-slate-500 mt-0.5">Track candidate responses and submit scorecards.</div></div>
                </Link>
                <Link to="/employer/offers" data-testid="cta-offers" className="p-4 border-2 border-emerald-300 rounded-lg hover:bg-emerald-50 flex items-start gap-3">
                  <PlusCircle size={20} className="text-emerald-700 shrink-0 mt-0.5"/>
                  <div><div className="font-semibold text-slate-900">Send an offer</div><div className="text-xs text-slate-500 mt-0.5">Draft, send, confirm hires. Candidate fees always $0.</div></div>
                </Link>
                <Link to="/employer/placements" data-testid="cta-placements" className="p-4 border-2 border-slate-400 rounded-lg hover:bg-slate-100 flex items-start gap-3">
                  <Briefcase size={20} className="text-slate-800 shrink-0 mt-0.5"/>
                  <div><div className="font-semibold text-slate-900">Placements & Workers</div><div className="text-xs text-slate-500 mt-0.5">Milestones, guarantee, replacement requests.</div></div>
                </Link>
              </div>
            </div>
          </div>
        )}

        {tab==="profile" && company && <CompanyProfileEditor company={company} onSaved={load}/>}
        {tab==="jobs" && <JobsList jobs={jobs} onRefresh={load}/>}
      </main>
      <Toaster position="top-right" richColors/>
    </div>
  );
}

const Stat = ({label, value, icon: Ic, onClick}) => (
  <button onClick={onClick} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm text-left hover:border-sky-300">
    <div className="flex items-center gap-2 mb-2"><Ic size={14} className="text-sky-600"/><span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</span></div>
    <div className="text-2xl font-display font-bold text-slate-900">{value}</div>
  </button>
);

const PROFILE_SECTIONS = [
  {title:"Basics",fields:[["legal_name","Legal / business name"],["description","Company description","textarea"],["products_services","Products & services","textarea"],["mission","Mission","textarea"],["values","Values (comma-separated)","tags"],["company_size","Company size"]]},
  {title:"Culture & Team",fields:[["workplace_culture","Workplace culture","textarea"],["management_style","Management style","textarea"],["team_environment","Team environment","textarea"],["communication_style","Communication style"],["work_pace","Work pace"],["remote_environment","Remote / hybrid / on-site environment"]]},
  {title:"Schedules & Tools",fields:[["typical_schedules","Typical schedules","textarea"],["technology_stack","Technology stack (comma-separated)","tags"],["equipment_provided","Equipment provided"]]},
  {title:"Benefits & Perks",fields:[["benefits","Benefits summary","textarea"],["pto","PTO"],["holidays","Holidays"],["health_dental_vision","Health / dental / vision"],["retirement_benefits","Retirement benefits"],["bonus_commission_programs","Bonus / commission programs"],["incentives","Incentives"],["perks","Perks / discounts"],["other_benefits","Other benefits"]]},
  {title:"Growth & Expectations",fields:[["training_provided","Training provided","textarea"],["advancement_opportunities","Advancement opportunities","textarea"],["performance_expectations","Performance expectations","textarea"],["first_30_60_90","First 30/60/90-day expectations","textarea"]]},
  {title:"Employer-friendly narrative",fields:[["what_its_like","What is it really like to work here?","textarea"],["who_succeeds_here","What kind of employee tends to succeed here?","textarea"],["what_employees_like","What do employees like about working here?","textarea"],["how_managers_work","How do managers work with their teams?","textarea"],["advancement_details","What opportunities exist for advancement?","textarea"]]},
];

function CompanyProfileEditor({company, onSaved}) {
  const [c, setC] = useState(company);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC(prev => ({...prev, [k]: v}));
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/companies/${c.id}`, c); toast.success("Company profile saved"); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      {PROFILE_SECTIONS.map(sec => (
        <section key={sec.title} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">{sec.title}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {sec.fields.map(([k, lbl, type]) => (
              <div key={k} className={type==="textarea"?"md:col-span-2":""}>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{lbl}</label>
                {type==="textarea" ? (
                  <textarea rows={3} data-testid={`cp-${k}`} value={c[k]||""} onChange={e=>set(k, e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/>
                ) : type==="tags" ? (
                  <input data-testid={`cp-${k}`} value={(c[k]||[]).join(", ")} onChange={e=>set(k, e.target.value.split(",").map(s=>s.trim()).filter(Boolean))} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/>
                ) : (
                  <input data-testid={`cp-${k}`} value={c[k]||""} onChange={e=>set(k, e.target.value)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
      <button data-testid="cp-save" onClick={save} disabled={saving} className="h-11 px-6 rounded-md bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">{saving && <Loader2 size={14} className="animate-spin"/>}<Save size={14}/> Save profile</button>
    </div>
  );
}

function JobsList({jobs, onRefresh}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between"><h3 className="font-semibold text-slate-900">Your job orders</h3>
        <Link to="/employer/job-builder" className="h-9 px-3 rounded bg-slate-900 text-white text-xs font-semibold inline-flex items-center gap-1.5" data-testid="new-job-btn"><PlusCircle size={13}/> New</Link></div>
      {jobs.length===0 ? <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-10 text-center">No job orders yet. Click <b>New</b> to create your first.</div> :
        jobs.map(j => (
          <Link key={j.id} to={`/employer/job-builder?edit=${j.id}`} data-testid={`job-row-${j.id}`} className="block bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-sky-300">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold text-slate-900">{j.role_title}</div>
                <div className="text-xs text-slate-500">{j.role_family || j.department || "—"} · created {new Date(j.created_at).toLocaleDateString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">{j.source_type || "-"}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${j.status==="Open"?"bg-emerald-50 border-emerald-200 text-emerald-800":j.status==="Draft"?"bg-slate-100 border-slate-200 text-slate-600":"bg-sky-50 border-sky-200 text-sky-700"}`}>{j.status}</span>
              </div>
            </div>
          </Link>
        ))
      }
    </div>
  );
}
